import cionNew from '@src/assets/base64/cion-new.js';
import { useCallback, useEffect, useId, useMemo, type Ref } from 'react';
import FJInput from '@src/common/components/FJInput/FJInput';
import QuestionIcon from '../../assets/question.svg?react';
import FJUsageDetailTwo, { type FJUsageDetailTwoProps } from '@src/common/components/FJUsageDetailTwo/FJUsageDetailTwo';
import {
    DURATION_LIST,
    GENRE_LIST,
    // ORIGINAL_LANGUAGE_LIST,
    RATIO_LIST,
    TARGET_LANGUAGE_LIST,
} from '../../config/FJVideoSettingDataConfig';
import FJDropdown from '@src/common/components/FJDropDown/FJDropDown.tsx';
import useLocalStore from '../../hooks/useLocalStore';
import FJLocalStore from '../../store/FJLocalStore';
import FJVideoMetaCard from '../FJVideoMetaCard/FJVideoMetaCard';
import FJGeneratingSettingsSelect from './FJGeneratingSettingsSelect';
import FJSubtitleList from './FJSubtitleList';
import styles from './FJGeneratingSettingsPanel.module.less';
import { FJButtonType } from '@src/common/components/FJButton/FJButton';
import FJButton from '@src/common/components/FJButton/FJButton';
import FJScroller from '@src/common/components/FJScroller/FJScroller';
import type { FJGeneratingVideoMeta } from '@src/types/FJProject';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import FJStarIcon from '@src/assets/star.svg?react';
import FJDropDownManager from '@src/common/js/FJDropDownManager';
interface FJGeneratingSettingsPanelProps {
    video: FJGeneratingVideoMeta;
    onRemoveVideo: () => void;
    originalLanguageKey: string;
    onOriginalLanguageKey: (key: string) => void;
    textLanguageKey: string;
    onTextLanguageKey: (key: string) => void;
    genreKey: string;
    onGenreKey: (key: string) => void;
    clipLengthKey: string;
    onClipLengthKey: (key: string) => void;
    aspectRatioKey: string;
    onAspectRatioKey: (key: string) => void;
    subtitleStyleId: string;
    onSubtitleStyleId: (id: string) => void;
    captureKeyMoments: string;
    onCaptureKeyMoments: (v: string) => void;
    onGenerate: () => void;
    freeNumber: number;
    totalCredits: number;
}

export default function FJGeneratingSettingsPanel({
    video,
    freeNumber,
    totalCredits,
    onRemoveVideo,
    // originalLanguageKey,
    // onOriginalLanguageKey,
    textLanguageKey,
    onTextLanguageKey,
    genreKey,
    onGenreKey,
    clipLengthKey,
    onClipLengthKey,
    aspectRatioKey,
    onAspectRatioKey,
    subtitleStyleId,
    onSubtitleStyleId,
    captureKeyMoments,
    onCaptureKeyMoments,
    onGenerate,
}: FJGeneratingSettingsPanelProps) {
    useLocalStore();
    const captureInputId = useId();
    const tag = FJLocalStore.currentTag();
    const durationMinutesRaw = useMemo(() => (video.duration ? video.duration / 60 : 0), [video.duration]);
    /** 展示用（分钟），保留两位小数 */
    const duration = useMemo(
        () => (durationMinutesRaw === 0 ? 0 : Number(durationMinutesRaw.toFixed(2))),
        [durationMinutesRaw],
    );
    const useCredit = useMemo(() => Math.floor(durationMinutesRaw), [durationMinutesRaw]);
    // const originalLanguageOptions = useMemo(
    //     () =>
    //         ORIGINAL_LANGUAGE_LIST.map(row => ({
    //             value: row.value,
    //             label: FJLocalStore._(row.text),
    //         })),
    //     [],
    // );
    const textLanguageOptions = useMemo(
        () => TARGET_LANGUAGE_LIST.map(lang => ({ value: lang.value, label: lang.text })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tag],
    );

    const genreOptions = useMemo(
        () => GENRE_LIST.map(row => ({ value: row.value, label: FJLocalStore._(row.text) })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tag],
    );
    /** 与 `video.duration`（秒）比较；不足最小时长则禁选对应项（见 `DURATION_LIST[].minSourceDurationSec`） */
    const sourceDurationSec = video.duration ?? 0;
    const clipOptions = useMemo(
        () =>
            DURATION_LIST.map(row => {
                const minSec = row.minSourceDurationSec;
                const disabled = minSec != null && sourceDurationSec < minSec;
                return {
                    value: row.value,
                    label: FJLocalStore._(row.text),
                    disabled,
                    tip: disabled ? FJLocalStore._('the-selected-range-exceeds-the-current-video-duration') : '',
                };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tag, sourceDurationSec],
    );

    /** 当前已选 90–180s 但源视频不足 90s 时，回退为 auto */
    useEffect(() => {
        const blocked = DURATION_LIST.some(
            row =>
                row.value === clipLengthKey &&
                row.minSourceDurationSec != null &&
                sourceDurationSec < row.minSourceDurationSec,
        );
        if (blocked) {
            onClipLengthKey('auto');
        }
    }, [clipLengthKey, sourceDurationSec, onClipLengthKey]);

    const ratioOptions = useMemo(
        () => RATIO_LIST.map(ratio => ({ value: ratio, label: ratio })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tag],
    );

    const usageDetailList = useMemo<FJUsageDetailTwoProps['list']>(
        () => [
            [
                {
                    label: FJLocalStore._('video'),
                    value: '1 ' + FJLocalStore._('credits-min'),
                },
                {
                    label: FJLocalStore._('duration'),
                    value: `${duration.toFixed(2)} ${FJLocalStore._('mins')}`,
                },
            ],
        ],
        [duration, tag],
    );
    const isLogin = FJRequirementsUtil.isLogin();
    const isUseFreeTimes = useMemo(() => {
        if (isLogin && totalCredits < useCredit && freeNumber > 0) {
            return true;
        }
        return false;
    }, [freeNumber, isLogin, totalCredits, useCredit]);
    const usageDetailTriggerContent = useMemo(() => {
        if (!isLogin) {
            return <div className={styles.free_creation_bottom}>{FJLocalStore._('free-try')}</div>;
        }
        if (isUseFreeTimes) {
            return (
                <div className={styles.free_creation_bottom}>
                    {freeNumber === 1
                        ? FJLocalStore._('1-free-try-left')
                        : FJLocalStore._('xxx-free-tries-left').replace('xxx', String(freeNumber))}
                </div>
            );
        }

        const creditText = useCredit > 0 ? `-${useCredit}` : '0';
        return (
            <div className={styles.use_credit}>
                <img src={cionNew} alt='' className={styles.credit_icon} />
                <span className={styles.use_credit_num}>{creditText}</span>
            </div>
        );
    }, [freeNumber, isLogin, useCredit, isUseFreeTimes]);

    const renderUsageDetailTrigger = useCallback<FJUsageDetailTwoProps['renderTrigger']>(
        ({ ref, onClick, onMouseMove, style }) => (
            <div ref={ref as Ref<HTMLDivElement>} style={style} onClick={onClick} onMouseMove={onMouseMove}>
                {usageDetailTriggerContent}
            </div>
        ),
        [usageDetailTriggerContent],
    );

    const _btnRightNode = useMemo(() => {
        if (isUseFreeTimes) {
            return usageDetailTriggerContent;
        }
        return (
            <FJUsageDetailTwo
                list={usageDetailList}
                totalCredit={useCredit}
                renderTrigger={renderUsageDetailTrigger}
                placement='rightTop'
            />
        );
    }, [usageDetailList, useCredit, renderUsageDetailTrigger, isUseFreeTimes, usageDetailTriggerContent]);
    const onScrollTriggerStart = useCallback(() => {
        FJDropDownManager.hideAll();
    }, []);
    return (
        <FJScroller className={styles.root} onScrollTriggerStart={onScrollTriggerStart}>
            <div className={styles.card_wrap_inner}>
                <div className={styles.card_wrap}>
                    <FJVideoMetaCard
                        thumbnailUrl={video.thumbnailUrl}
                        title={video.title}
                        sourceUrl={video.sourceUrl}
                        resolutionLabel={video.resolutionLabel}
                        onClose={onRemoveVideo}
                        emphasizeTitle
                    />
                </div>

                <div className={styles.grid}>
                    {/* <div className={styles.field}>
                        <span className={styles.field_label}>{FJLocalStore._('original-language')}</span>
                        <FJGeneratingSettingsSelect
                            ariaLabel={FJLocalStore._('original-language')}
                            value={originalLanguageKey}
                            options={originalLanguageOptions}
                            onChange={onOriginalLanguageKey}
                        />
                    </div> */}

                    <div className={styles.field}>
                        <span className={styles.field_label}>{FJLocalStore._('text-language')}</span>
                        <FJGeneratingSettingsSelect
                            ariaLabel={FJLocalStore._('text-language')}
                            value={textLanguageKey}
                            options={textLanguageOptions}
                            onChange={onTextLanguageKey}
                        />
                    </div>

                    <div className={styles.field}>
                        <span className={styles.field_label}>{FJLocalStore._('target-genre')}</span>
                        <FJGeneratingSettingsSelect
                            ariaLabel={FJLocalStore._('target-genre')}
                            value={genreKey}
                            options={genreOptions}
                            onChange={onGenreKey}
                        />
                    </div>

                    <div className={styles.field}>
                        <span className={styles.field_label}>{FJLocalStore._('duration')}</span>
                        <FJGeneratingSettingsSelect
                            ariaLabel={FJLocalStore._('duration')}
                            value={clipLengthKey}
                            options={clipOptions}
                            onChange={onClipLengthKey}
                        />
                    </div>

                    <div className={styles.field}>
                        <span className={styles.field_label}>{FJLocalStore._('ratio')}</span>
                        <FJGeneratingSettingsSelect
                            ariaLabel={FJLocalStore._('ratio')}
                            value={aspectRatioKey}
                            options={ratioOptions}
                            onChange={onAspectRatioKey}
                        />
                    </div>
                </div>

                <FJSubtitleList selectedId={subtitleStyleId} onSelect={onSubtitleStyleId} />

                <div className={styles.capture_block}>
                    <span className={styles.capture_label_row}>
                        <label className={styles.field_label} htmlFor={captureInputId}>
                            {FJLocalStore._('capture-specific-moments')}
                        </label>
                        <FJDropdown
                            isTopLayer
                            useAnchorBody
                            trigger='hover'
                            ignoreManager
                            placement='rightTop'
                            triggerDisplay='inline-flex'
                            offsetLeft={5}
                            overlayClassName={styles.help_popover_overlay}
                            renderTrigger={({ ref, onClick, onMouseMove, style }) => (
                                <span
                                    ref={ref as Ref<HTMLSpanElement>}
                                    style={style}
                                    className={styles.help_dropdown_trigger}
                                    onClick={onClick}
                                    onMouseMove={onMouseMove}
                                >
                                    <QuestionIcon className={styles.help_mark} aria-hidden />
                                </span>
                            )}
                            renderOverlay={({ ref }) => (
                                <div
                                    ref={ref as Ref<HTMLDivElement>}
                                    className={styles.help_popover_content}
                                    onClick={e => e.stopPropagation()}
                                >
                                    {FJLocalStore._('add-the-moments-people-and-keywords-you-want-to-keep')}
                                </div>
                            )}
                            active
                        />
                    </span>
                    <FJInput
                        id={captureInputId}
                        value={captureKeyMoments}
                        onChange={e => onCaptureKeyMoments(e.target.value)}
                        placeholder={FJLocalStore._('select-the-highlights')}
                        autoComplete='off'
                        maxLength={100}
                    />
                </div>
            </div>
            <div className={styles.generate_row}>
                <FJButton
                    type={FJButtonType.GRADIENT}
                    className={styles.generate_btn}
                    textClassName={styles.generate_btn_text}
                    icon={<FJStarIcon className={styles.generate_icon} aria-hidden />}
                    text='generate'
                    onClick={onGenerate}
                    rightNode={_btnRightNode}
                />
            </div>
        </FJScroller>
    );
}
